import { useContext, useEffect, useState } from "react";
import useStateRef from "react-usestateref";
import { staticCharacterListWithBios } from "../services/characters/static-character-list-with-bios";
import { BrowserVoiceInfo, getBrowserVoices } from "../services/speech/get-browser-voices";
import { logger } from "../utils/logger";
import { defaultMultiLingualId, useSettings } from "./use-settings";
import { TutorContext } from "./use-tutor";
import { isInWebView } from "utils/is-webview";

export function useVoicePreference() {
  const { currentTutor } = useContext(TutorContext);
  const { settings, settingsAreLoading } = useSettings();
  const [browserVoicesLoaded, setBrowserVoicesLoaded] = useState<boolean>(false);
  const [browserVoices, setBrowserVoices] = useState<BrowserVoiceInfo[]>();
  const [browserVoice, setBrowserVoice, browserVoiceRef] = useStateRef<BrowserVoiceInfo>();

  const [voicePreference, setVoicePreference] = useState<string>(defaultMultiLingualId);

  const shouldUseBrowserVoice = currentTutor ? false : Boolean(browserVoice) && !isInWebView();

  useEffect(() => {
    if (isInWebView()) {
      // In RN WebView, avoid querying system TTS voices
      setBrowserVoices([]);
      setBrowserVoicesLoaded(true);
      return;
    }
    getBrowserVoices()
      .then((browserVoices) => {
        setBrowserVoices(browserVoices);
      })
      .catch((err) => {
        logger.error("Failed to get browser voices");
      })
      .finally(() => {
        setBrowserVoicesLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (settingsAreLoading || !settings.voicePreference || !browserVoicesLoaded) {
      return;
    }

    const character = staticCharacterListWithBios.find((c) => c.id === settings.voicePreference);

    const isBrowserVoice = Boolean(character?.browserVoice);

    if (!isBrowserVoice || isInWebView()) {
      setVoicePreference(settings.voicePreference);
      return;
    }

    const browserVoice = browserVoices?.find(
      (voice) => voice.browserVoiceCharacter.id === settings.voicePreference,
    );

    if (
      !browserVoice?.browserVoiceCharacter.browserVoice ||
      !browserVoice.speechSynthesisVoice.voiceURI
    ) {
      if (!character?.browserVoice?.azureURI) {
        logger.error("Browser voice not found. This should not happen!");
        setVoicePreference(defaultMultiLingualId);
        return;
      }

      logger.error("Browser not supporting TTS. Using Azure voice.");
      setVoicePreference(character.browserVoice.azureURI);
      return;
    }

    logger.info("Using browser voice");
    setBrowserVoice(browserVoice);
    setVoicePreference(browserVoice.speechSynthesisVoice.voiceURI);
  }, [
    browserVoices,
    browserVoice,
    browserVoicesLoaded,
    settings.voicePreference,
    settingsAreLoading,
    setBrowserVoice,
  ]);

  return {
    shouldUseBrowserVoice,
    voicePreference,
    browserVoice,
    browserVoiceRef,
  };
}
